import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { API } from '../../../services/api';
import Filters from '../../../services/filters';

import { AuthContext } from '../../../contexts/auth';
import { PaginationContext } from '../../../contexts/pagination';

import Alert from '../../Modals/Alert';
import Confirmation from '../../Modals/Confirmation';
import CheckboxFilter from '../../Filters/CheckboxFilter';
import ListRows from './ListRows';
import RadioFilter from '../../Filters/RadioFilter';
import TablePagination from '../../TablePagination';
import TextFilter from '../../Filters/TextFilter';

import '../../../style.css';

function UsersList({ users, setSelectedUser, setUpdateList }) {
  const {
    activeUser,
    token,
  } = useContext(AuthContext);

  const { sliceCurrentList } = useContext(PaginationContext);

  const [alertMessage, setAlertMessage] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [currentList, setCurrentList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [sortOrder, setSortOrder] = useState({});

  const filter = useRef({});
  const operation = useRef();
  const userId = useRef();

  function handleSort(column) {
    const currentOrder = sortOrder[column] || 'asc';

    const sortedData = [...users];

    sortedData.sort((a, b) => {
      const aValue = a[column] || '';
      const bValue = b[column] || '';

      if (column === 'active') {
        return currentOrder === 'asc'
          ? (aValue ? 'Ativo' : 'Inativo').localeCompare((bValue ? 'Ativo' : 'Inativo'))
          : (bValue ? 'Ativo' : 'Inativo').localeCompare((aValue ? 'Ativo' : 'Inativo'));
      }

      return currentOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    setCurrentList(sortedData);
    setSortOrder({ ...sortOrder, [column]: currentOrder === 'asc' ? 'desc' : 'asc' });
  }

  function handleFilterChange() {
    if (Object.keys(filter.current).length === 0) {
      setNoResultsFound(false);
      setCurrentList(users);
      return;
    }

    let tempList = users;

    tempList = tempList.filter((item) => {
      const nameMatch = Filters.filterText(filter.current.name, item.name);
      const emailMatch = Filters.filterText(filter.current.email, item.email);
      const categoryMatch = Filters.filterData(filter.current.category, item.category);
      const activeMatch = Filters.filterData(filter.current.active, (item.active ? 'Ativo' : 'Inativo'));

      return nameMatch && emailMatch && categoryMatch && activeMatch;
    });

    setNoResultsFound(tempList.length === 0);

    setCurrentList(tempList);
  }

  function handleConfirmRemoveUser(user) {
    setConfirmationMessage(`Tem certeza de que deseja remover/inativar o usuário ${user.name} (${user.email})?`);
    operation.current = 'DELETE';
    userId.current = user.id;
    setOpenConfirmation(true);
  }

  function handleConfirmActivateUser(user) {
    setConfirmationMessage(`Tem certeza de que deseja ativar o usuário ${user.name} (${user.email})?`);
    operation.current = 'PUT';
    userId.current = user.id;
    setOpenConfirmation(true);
  }

  const manageUserStatus = useCallback(async () => {
    try {
      const reqData = {
        id: userId.current,
        token,
      };

      if (operation.current === 'PUT') {
        reqData.body = { active: true };
      }

      const data = await API.fetch(operation.current, 'users', reqData);

      setAlertMessage({
        title: 'Sucesso!',
        message: data,
      });

      setOpenAlert(true);

      setUpdateList(true);
    } catch (error) {
      setAlertMessage({
        title: 'Erro!',
        message: error.name === 'APIError' ? error.message : `Erro de sistema: ${error.message}`,
      });

      setOpenAlert(true);

      if (error.name !== 'APIError') {
        const errorData = {
          dateTime: new Date(),
          user: activeUser,
          response: error.response,
          origin: 'src/components/Lists/Users/index.js - manageUserStatus',
        };
        console.log('Enviar errorData para registro no back-end');
      }
    }
  }, [token, setUpdateList]);

  useEffect(() => {
    if (confirmation) {
      manageUserStatus();
      setConfirmation(false);
      operation.current = '';
      userId.current = '';
    }
  }, [confirmation, manageUserStatus]);

  useEffect(() => {
    setCurrentList(users);
  }, [users]);

  return (
    <div className='table-component-container'>
      <div className='table-component-table-area'>
        <table className='table-component-table-body'>
          <thead>
            <th
              className='table-component-table-th table-component-table-th-first-column'
              style={{
                width: 400,
                minWidth: 400,
                maxWidth: 400,
              }}
            >
              <span className='table-component-table-header'>
                NOME
                <span className='table-component-table-filter-area'>
                  <>
                    <span
                      className='table-component-sort-button'
                      onClick={() => handleSort('name')}
                    >
                      {sortOrder.name === 'asc' ? '▲' : '▼'}
                    </span>
                    <TextFilter
                      applyFilters={handleFilterChange}
                      column='name'
                      filter={filter}
                      active={filter.current?.name?.length > 0}
                    />
                  </>
                </span>
              </span>
            </th>
            <th
              className='table-component-table-th'
              style={{
                minWidth: 400,
                maxWidth: 'auto',
              }}
            >
              <span className='table-component-table-header'>
                E-MAIL
                <span className='table-component-table-filter-area'>
                  <>
                    <span
                      className='table-component-sort-button'
                      onClick={() => handleSort('email')}
                    >
                      {sortOrder.email === 'asc' ? '▲' : '▼'}
                    </span>
                    <TextFilter
                      applyFilters={handleFilterChange}
                      column='email'
                      filter={filter}
                      active={filter.current?.email?.length > 0}
                    />
                  </>
                </span>
              </span>
            </th>
            <th
              className='table-component-table-th'
              style={{
                width: 140,
                minWidth: 140,
                maxWidth: 140,
              }}
            >
              <span className='table-component-table-header'>
                CATEGORIA
                <span className='table-component-table-filter-area'>
                  <>
                    <span
                      className='table-component-sort-button'
                      onClick={() => handleSort('category')}
                    >
                      {sortOrder.category === 'asc' ? '▲' : '▼'}
                    </span>
                    <CheckboxFilter
                      applyFilters={handleFilterChange}
                      filterOptions={[...new Set(users?.map((item) => item.category))]}
                      column='category'
                      filter={filter}
                      active={filter.current?.category?.length > 0}
                    />
                  </>
                </span>
              </span>
            </th>
            <th
              className='table-component-table-th'
              style={{
                width: 110,
                minWidth: 110,
                maxWidth: 110,
              }}
            >
              <span className='table-component-table-header'>
                STATUS
                <span className='table-component-table-filter-area'>
                  <>
                    <span
                      className='table-component-sort-button'
                      onClick={() => handleSort('active')}
                    >
                      {sortOrder.active === 'asc' ? '▲' : '▼'}
                    </span>
                    <RadioFilter
                      applyFilters={handleFilterChange}
                      filterOptions={['Ativo', 'Inativo']}
                      column='active'
                      filter={filter}
                      active={filter.current?.active?.length > 0}
                    />
                  </>
                </span>
              </span>
            </th>
            <th
              className='table-component-table-th table-component-table-th-last-column'
              style={{
                width: 60,
                minWidth: 60,
                maxWidth: 60,
              }}
            />
          </thead>
          <tbody
            className='table-component-rows-area'
            style={{ height: '100px' }}
          >
            {(users.length > 0) && (
              sliceCurrentList(currentList, currentPage).map((user) => (
                <ListRows
                  key={user.id}
                  user={user}
                  setSelectedUser={setSelectedUser}
                  handleConfirmRemoveUser={handleConfirmRemoveUser}
                  handleConfirmActivateUser={handleConfirmActivateUser}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {noResultsFound && (
        <div className='table-component-no-results'>
          FILTRO APLICADO NÃO APRESENTOU RESULTADOS
        </div>
      )}
      <TablePagination
        array={currentList}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      {(openConfirmation && confirmationMessage) && (
        <Confirmation
          openConfirmation={openConfirmation}
          setOpenConfirmation={setOpenConfirmation}
          confirmationMessage={confirmationMessage}
          setConfirmationMessage={setConfirmationMessage}
          setConfirmation={setConfirmation}
        />
      )}
      {openAlert && (
        <Alert
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          alertMessage={alertMessage}
          setAlertMessage={setAlertMessage}
        />
      )}
    </div>
  );
}

export default UsersList;
